/**
 * Side-panel dialog shell for the AdvancedPanel.
 *
 * Per UX decision: the user picked the dialog over the inline accordion. The
 * inline variant has been removed. The advanced panel always lives in this
 * side-panel dialog.
 */

import type { QueueStore } from '@/lib/queue/store';
import { createAdvancedPanel } from './AdvancedPanel';
import { isLoaded as isPackLoaded, loadAdvancedPack } from '@/lib/advanced/pack-loader';
import {
  DEFAULT_FILTERS, DEFAULT_GIF_ADVANCED, DEFAULT_WEBP_ADVANCED,
  DEFAULT_PNG_ADVANCED, DEFAULT_JPEG_ADVANCED, DEFAULT_AVIF_ADVANCED,
  DEFAULT_MP4_ADVANCED,
  type PerFileSettings,
} from '@/lib/queue/store';

export interface AdvancedShellHandle {
  openDialog(): void;
  closeDialog(): void;
}

export function createAdvancedShell(store: QueueStore): AdvancedShellHandle {
  const panel = createAdvancedPanel(store);

  const backdrop = document.createElement('div');
  backdrop.className = 'rd-modal-backdrop adv-dialog-backdrop';
  backdrop.setAttribute('role', 'dialog');
  backdrop.setAttribute('aria-modal', 'false');
  backdrop.setAttribute('aria-label', 'Editor');

  const dialog = document.createElement('div');
  dialog.className = 'rd-modal adv-dialog';

  const dialogHeader = document.createElement('div');
  dialogHeader.className = 'rd-modal__header';
  const dialogTitle = document.createElement('h2');
  dialogTitle.className = 'rd-modal__title';
  dialogTitle.textContent = 'Editor';
  const dialogClose = document.createElement('button');
  dialogClose.type = 'button';
  dialogClose.className = 'rd-modal__close';
  dialogClose.setAttribute('aria-label', 'Close');
  dialogClose.textContent = '×';
  dialogHeader.append(dialogTitle, dialogClose);

  const dialogBody = document.createElement('div');
  dialogBody.className = 'rd-modal__content adv-dialog__content';
  dialogBody.appendChild(panel);

  dialog.append(dialogHeader, dialogBody);
  backdrop.appendChild(dialog);

  function openDialog(): void {
    if (backdrop.parentElement) return;
    autoEnablePosterizeForIndexed();
    document.body.appendChild(backdrop);
    store.setAdvancedUi({ dialogOpen: true });
    // Auto-load the advanced pack when the dialog opens — saves the user a
    // click. When the chunks are already in the browser's HTTP cache the
    // load resolves near-instantly so the UI feels seamless on reopen.
    void autoLoadAdvancedPack();
  }

  async function autoLoadAdvancedPack(): Promise<void> {
    if (isPackLoaded()) return;
    const ui = store.getAdvancedUi();
    if (ui.pack.kind === 'loading' || ui.pack.kind === 'ready') return;
    store.setAdvancedUi({ pack: { kind: 'loading' } });
    try {
      await loadAdvancedPack();
      // Seed defaults the same way the manual gate button does so the
      // encoder/format-specific panels render once loaded.
      const cur = store.getGlobalDefaults();
      const patch: Partial<PerFileSettings> = {};
      if (!cur.filters) patch.filters = { ...DEFAULT_FILTERS };
      if (!cur.gif) patch.gif = { ...DEFAULT_GIF_ADVANCED };
      if (!cur.webp) patch.webp = { ...DEFAULT_WEBP_ADVANCED };
      if (!cur.png) patch.png = { ...DEFAULT_PNG_ADVANCED };
      if (!cur.jpeg) patch.jpeg = { ...DEFAULT_JPEG_ADVANCED };
      if (!cur.avif) patch.avif = { ...DEFAULT_AVIF_ADVANCED };
      if (!cur.mp4) patch.mp4 = { ...DEFAULT_MP4_ADVANCED };
      if (Object.keys(patch).length > 0) store.setGlobalDefaults(patch);
      store.setAdvancedUi({ pack: { kind: 'ready' } });
    } catch (err) {
      store.setAdvancedUi({ pack: { kind: 'error', reason: (err as Error).message } });
    }
  }

  /** Indexed-palette inputs (GIF, indexed PNG) get Posterize enabled by default
   *  on first edit so the user immediately sees palette controls. We only flip
   *  the bit when posterize is currently off, so an explicit "off" choice is
   *  respected on subsequent opens. */
  function autoEnablePosterizeForIndexed(): void {
    const { selectedSourceId, items, globalDefaults } = store.getState();
    const src = items.find(i => i.id === selectedSourceId && i.isSource)
      ?? items.find(i => i.isSource);
    if (!src) return;
    const type = (src.file.type || '').toLowerCase();
    const name = (src.file.name || '').toLowerCase();
    const isIndexed = type === 'image/gif' || name.endsWith('.gif') || name.endsWith('.bmp');
    if (!isIndexed) return;
    const cur = globalDefaults.filters?.posterize ?? 0;
    if (cur > 0) return;
    const baseFilters = globalDefaults.filters ?? {
      brightness: 0, contrast: 0, saturation: 0,
      invert: false, grayscale: false,
      posterize: 0,
      posterizeFromImage: true,
      dither: 'floyd-steinberg' as const,
    };
    store.setGlobalDefaults({
      filters: { ...baseFilters, posterize: 32 },
    });
  }
  function closeDialog(): void {
    if (!backdrop.parentElement) return;
    backdrop.parentElement.removeChild(backdrop);
    store.setAdvancedUi({ dialogOpen: false });
  }
  dialogClose.addEventListener('click', closeDialog);
  backdrop.addEventListener('click', (e) => { if (e.target === backdrop) closeDialog(); });
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && backdrop.parentElement) closeDialog();
  });

  return { openDialog, closeDialog };
}
